import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {guestClient} from '../services/rest';
import kioskApi from '../config/kioskApiConfig';
import {setAvailableLanguages} from '../services/localization';

import {sendCredentials, updateUser} from './user.slice';
import {fetchQuestionnaire} from './questionnaire.slice';
import {
  clearPickedFiles,
  reset,
  resetAnswers,
  sendQuestionnaireResponse,
  sendReport,
} from './sharedActions';
import translate, {setI18nConfig} from "../services/localization";
import localStorage from "../services/localStorage";
import AlertModal from "../components/common/AlertModal";

// services & config

const getLanguages = createAsyncThunk(
    'globals/GET_LANGUAGES',
    async (_, thunkApi) => {
        const languages = await guestClient.getLanguages();
        setAvailableLanguages(languages);
        return thunkApi.fulfillWithValue(languages);
    },
);

const setBLEBatteryLevel = createAsyncThunk(
    'globals/SET_BLE_BATTERY_LEVEL',
    async (batteryLevel, thunkApi) => {
        return thunkApi.fulfillWithValue(batteryLevel);
    },
);

const setBLEBufferSize = createAsyncThunk(
    'globals/SET_BUFFER_SIZE',
    async (bufferSize, thunkApi) => {
        return thunkApi.fulfillWithValue(bufferSize);
    },
);

const setNewVersionURL = createAsyncThunk(
  'globals/SET_NEW_VERSION_URL',
  async (newVersionURL, thunkApi) => {
    return thunkApi.fulfillWithValue(newVersionURL);
  },
);


const setBLErssi = createAsyncThunk(
    'globals/SET_BLE_RSSI',
    async (batteryLevel, thunkApi) => {
        return thunkApi.fulfillWithValue(batteryLevel);
    },
);

const setBLEDevice = createAsyncThunk(
    'globals/SET_BLE_DEVICE',
    async (device, thunkApi) => {
        console.log('setting connected BLE device');
        console.log(device)
        console.log('device id')
        console.log(device.id)
        console.log(device.name)
        return thunkApi.fulfillWithValue(device);
    },
);

const setBLEDeviceState = createAsyncThunk(
    'globals/SET_BLE_DEVICE_STATE',
    async (batteryLevel, thunkApi) => {
        return thunkApi.fulfillWithValue(batteryLevel);
    },
);


const updateLanguage = createAsyncThunk(
    'globals/UPDATE_LANGUAGE',
    async ({ subjectId, languageTag }, thunkApi) => {
        try {
            // await (isKioskMode
            //     ? kioskApi.updateLanguageCode(subjectId, languageTag)
            //     : loggedInClient.updateLanguageCode(subjectId, languageTag));
            setI18nConfig(languageTag);
            await localStorage.persistUserLanguage(languageTag);
            return thunkApi.fulfillWithValue(languageTag);
        } catch (err) {
            console.log('user.slice.js:updateLanguage')
            console.log(error);
            console.log(error.message);
            AlertModal.alert(
                translate('generic').errorTitle,
                translate('generic').updateError,
                [
                    {
                        text: translate('generic').ok,
                        onPress: () => { AlertModal.hide(); }
                    },
                ],
                { cancelable: false },
            );
            return thunkApi.rejectWithValue({
                error: {
                    code: err.code ?? 'ERROR',
                    message: err.message,
                    failedAction: 'user/UPDATE_LANGUAGE',
                },
            });
        }
    },
);

const setURLPassthrough =  createAsyncThunk(
    'globals/URLPASSTHROUGH',
    async (passthrough, thunkApi) => {
        return thunkApi.fulfillWithValue(passthrough);
    }
);



const setFontScaleFactor = createAsyncThunk(
    'globals/SET_FONT_SCALE_FACTOR',
    async (fontScaleFactor, thunkApi) => {
        return thunkApi.fulfillWithValue(fontScaleFactor);
    },
);

const setPickedFiles = createAsyncThunk(
  'globals/SET_PICKED_FILES',
  async (file, thunkApi) => {
    console.log('set picked files', file);
    return thunkApi.fulfillWithValue(file);
  },
);

/**
 * this part of the global state handles the loading state and all errors (mostly network related) that might occur
 */
const initialState = {
    loading: true,
    error: null,
    availableLanguages: null,
    BLEBatteryLevel: null,
    BLEdevice: null,
    BLEdeviceState: null,
    BLErssi: null,
    BLEbufferSize: null,
    lastSubmitSingle: {},
    fontScaleFactor: 1,
    language_code: null,
    URLPassthrough: [],
    pickedFiles: [],
}
const GlobalsSlice = createSlice({
    name: 'globals',
    initialState: initialState,
    reducers: {
        INIT: (state) => ({...initialState, loading: false}),
    },
    extraReducers: (builder) => {
        builder
            .addCase(sendCredentials.pending, (state) => ({
                ...state,
                loading: true,
            }))
            .addCase(sendCredentials.fulfilled, (state) => ({
                ...state,
                loading: false,
                error: null,
            }))
            .addCase(sendCredentials.rejected, (state, action) => ({
                ...state,
                loading: false,
                error: action.payload.error,
            }))
            .addCase(updateUser.pending, (state) => ({
                ...state,
                loading: true,
            }))
            .addCase(updateUser.fulfilled, (state) => ({
                ...state,
                loading: false,
                error: null,
            }))
            .addCase(updateUser.rejected, (state, action) => ({
                ...state,
                loading: false,
                error: action.payload.error,
            }))
            .addCase(updateLanguage.pending, (state) => ({...state, loading: true}))
            .addCase(updateLanguage.fulfilled, (state, action) => ({
                ...state,
                language_code: action.payload,
                loading: false,
                error: null,
            }))
            .addCase(updateLanguage.rejected, (state, action) => ({
                ...state,
                loading: false,
                error: action.payload?.error,
            }))
            .addCase(fetchQuestionnaire.pending, (state) => ({
                ...state,
                loading: true,
            }))
            .addCase(fetchQuestionnaire.fulfilled, (state) => ({
                ...state,
                loading: false,
                error: null,
            }))
            .addCase(fetchQuestionnaire.rejected, (state, action) => ({
                ...state,
                loading: false,
                error: action.payload.error,
            }))
            .addCase(setURLPassthrough.fulfilled, (state, action) => ({
                ...state,
                URLPassthrough: action.payload
            }))
            .addCase(sendQuestionnaireResponse.pending, (state) => ({
                ...state,
                loading: true,
            }))
            .addCase(sendQuestionnaireResponse.fulfilled, (state, action) => {
                let lss = {
                    ...state.lastSubmitSingle
                };
                if (action.payload.instrument_name) {
                    lss[action.payload.instrument_name] = new Date();
                }

                return {
                    ...state,
                    lastSubmitSingle: lss,
                    loading: false,
                    error: null,
                }
            })
            .addCase(resetAnswers.pending, (state) => ({
                ...state,
                loading: true,
            }))
            .addCase(resetAnswers.fulfilled, (state) => ({
                loading: false,
                error: null
            }))
            .addCase(sendQuestionnaireResponse.rejected, (state, action) => {
                return {
                    ...state,
                    loading: false,
                    error: action.payload ? action.payload.error : action.error,
                };
            })
            .addCase(reset.pending, (state) => ({
                ...state,
                loading: true,
            }))
            .addCase(reset.rejected, (state, action) => ({
                ...state,
                loading: false,
                error: action?.payload?.error,
            }))
            .addCase(reset.fulfilled, (_state) => ({
                loading: false,
                error: null,
                availableLanguages: null,
            }))
            .addCase(sendReport.pending, (state) => ({...state, loading: true}))
            .addCase(sendReport.fulfilled, (state) => ({
                ...state,
                loading: false,
                error: null,
            }))
            .addCase(sendReport.rejected, (state, action) => ({
                ...state,
                loading: false,
                error: action.payload.error,
            }))
            .addCase(getLanguages.fulfilled, (state, action) => ({
                ...state,
                availableLanguages: action.payload,
            }))
            .addCase(setBLEBatteryLevel.fulfilled, (state, action) => ({
                ...state,
                BLEBatteryLevel: action.payload,
            }))
            .addCase(setBLEDeviceState.fulfilled, (state, action) => ({
                ...state,
                BLEdeviceState: action.payload,
            }))
            .addCase(setBLEDevice.fulfilled, (state, action) => ({
                ...state,
                BLEdevice: action.payload,
            }))
            .addCase(setBLErssi.fulfilled, (state, action) => ({
                ...state,
                BLErssi: action.payload,
            }))

            .addCase(setBLEBufferSize.fulfilled, (state, action) => ({
                ...state,
                BLEbufferSize: action.payload,
            }))
            .addCase(setFontScaleFactor.fulfilled, (state, action) => ({
                ...state,
                fontScaleFactor: action.payload,
            }))
            .addCase(setPickedFiles.fulfilled, (state, action) => {
                let linkId = action.payload.linkId;
                let files = action.payload.pickedFiles;
                console.log('picked files set', linkId, files);
                return {
                    ...state,
                    pickedFiles: {
                        ...state.pickedFiles,
                        [linkId]: files
                    }
                };
            })
            .addCase(setPickedFiles.rejected, (state, action) => {
                console.log('picked files rejected', key, files);
                return state;
            })
            .addCase(clearPickedFiles, (state) => {
                state.pickedFiles = []; // Clear picked files
            })
            .addDefaultCase((state) => ({...state}));
    },
});

export default GlobalsSlice.reducer;
export {getLanguages, setBLEBatteryLevel, setBLEDevice, setBLEDeviceState, setBLErssi, setBLEBufferSize,
    setNewVersionURL, setFontScaleFactor, updateLanguage, setURLPassthrough,
    setPickedFiles
};
export const {INIT: init} = GlobalsSlice.actions;
